import { useState, useEffect, useRef, useMemo, type ComponentType, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import {
  Search,
  Star,
  FileText,
  DollarSign,
  Truck,
  Box,
  Users,
  BarChart3,
  ChevronRight,
  Scale,
  Table2,
  TrendingUp,
  LayoutDashboard,
  Package,
  Landmark,
} from 'lucide-react';

interface ReportItem {
  id: string;
  title: string;
  description: string;
  link?: string;
  icon: ComponentType<{ className?: string }>;
}

interface ReportCategory {
  id: string;
  title: string;
  icon: ComponentType<{ className?: string }>;
  reports: ReportItem[];
}

interface FinancialStatement {
  id: string;
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
}

const FAVORITES_KEY = 'brix_reports_favorites';
const LEGACY_FAVORITES_KEY = 'qbo_reports_favorites';

const loadStringIdsFromStorage = (key: string): string[] => {
  try {
    const saved = localStorage.getItem(key);
    if (!saved) return [];
    const parsed: unknown = JSON.parse(saved);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

const loadFavorites = (): string[] => {
  const current = loadStringIdsFromStorage(FAVORITES_KEY);
  if (current.length > 0) return current;

  const legacy = loadStringIdsFromStorage(LEGACY_FAVORITES_KEY);
  if (legacy.length > 0) {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(legacy));
    localStorage.removeItem(LEGACY_FAVORITES_KEY);
  }
  return legacy;
};

const categories: ReportCategory[] = [
  {
    id: 'business-overview',
    title: 'Business overview',
    icon: BarChart3,
    reports: [
      {
        id: 'financial-summary',
        title: 'Financial Summary',
        description: 'Income, expenses, VAT, and profit margins at a glance.',
        icon: LayoutDashboard,
        link: '/dashboard',
      },
      {
        id: 'custom-report',
        title: 'Custom Report',
        description: 'Tailored reports with custom date ranges and filters.',
        icon: FileText,
      },
    ],
  },
  {
    id: 'who-owes-you',
    title: 'Who owes you',
    icon: Users,
    reports: [
      {
        id: 'customer-balance-summary',
        title: 'Customer Balance Summary',
        description: 'Customer balances and open invoice activity.',
        icon: Users,
        link: '/reports/customer-balance-summary',
      },
    ],
  },
  {
    id: 'what-you-owe',
    title: 'What you owe',
    icon: Truck,
    reports: [
      {
        id: 'vendor-balance-summary',
        title: 'Vendor Balance Summary',
        description: 'Vendor balances and open bill activity.',
        icon: Truck,
        link: '/reports/vendor-balance-summary',
      },
    ],
  },
  {
    id: 'sales-customers',
    title: 'Sales and customers',
    icon: DollarSign,
    reports: [
      {
        id: 'sales-by-customer-summary',
        title: 'Sales by Customer Summary',
        description: 'Total sales per customer for a date range.',
        icon: Users,
        link: '/reports/sales-by-customer-summary',
      },
      {
        id: 'income-by-customer-summary',
        title: 'Income by Customer Summary',
        description: 'Gross profit (revenue minus COGS) per customer for a date range.',
        icon: TrendingUp,
        link: '/reports/income-by-customer-summary',
      },
      {
        id: 'sales-by-product-summary',
        title: 'Sales by Product Summary',
        description: 'Quantity and revenue per item or service.',
        icon: Box,
        link: '/reports/sales-by-product-summary',
      },
    ],
  },
  {
    id: 'inventory',
    title: 'Inventory',
    icon: Package,
    reports: [
      {
        id: 'inventory-report',
        title: 'Inventory Transactions',
        description: 'Stock movements, running balances, and valuations.',
        icon: Box,
        link: '/inventory-transactions',
      },
      {
        id: 'bank-collateral-stock',
        title: 'Bank Collateral Stock',
        description: 'FIFO cost vs catalog retail for lender collateral submissions.',
        icon: Landmark,
        link: '/reports/bank-collateral-stock',
      },
    ],
  },
];

const allReports = categories.flatMap((c) =>
  c.reports.map((r) => ({ ...r, categoryTitle: c.title, categoryId: c.id }))
);

const reportById = new Map(allReports.map((r) => [r.id, r]));

const Reports = () => {
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState('');

  const [favorites, setFavorites] = useState<string[]>(loadFavorites);

  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const goToAccountsTab = (
    activeTab: 'income-statement' | 'trial-balance' | 'balance-sheet'
  ) => {
    navigate('/accounts', { state: { activeTab } });
  };

  const financialStatements: FinancialStatement[] = [
    {
      id: 'income-statement',
      title: 'Income Statement',
      description: 'Profit & loss by period',
      icon: TrendingUp,
      onClick: () => goToAccountsTab('income-statement'),
    },
    {
      id: 'trial-balance',
      title: 'Trial Balance',
      description: 'Debits and credits as of a date',
      icon: Table2,
      onClick: () => goToAccountsTab('trial-balance'),
    },
    {
      id: 'balance-sheet',
      title: 'Balance Sheet',
      description: 'Assets, liabilities, and equity',
      icon: Scale,
      onClick: () => goToAccountsTab('balance-sheet'),
    },
  ];

  const openReport = (report: ReportItem) => {
    if (!report.link) return;
    navigate(report.link);
  };

  const toggleFavorite = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const matchesSearch = (report: ReportItem, categoryTitle: string) => {
    if (!normalizedQuery) return true;
    return (
      report.title.toLowerCase().includes(normalizedQuery) ||
      report.description.toLowerCase().includes(normalizedQuery) ||
      categoryTitle.toLowerCase().includes(normalizedQuery)
    );
  };

  const favoriteReports = favorites
    .map((id) => reportById.get(id))
    .filter((r): r is (typeof allReports)[number] => Boolean(r));

  const filteredCount = useMemo(
    () =>
      categories.reduce(
        (n, c) => n + c.reports.filter((r) => matchesSearch(r, c.title)).length,
        0
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [normalizedQuery]
  );

  const ReportCard = ({
    report,
    showCategory,
    categoryTitle,
  }: {
    report: (typeof allReports)[number];
    showCategory?: boolean;
    categoryTitle?: string;
  }) => {
    const isReady = Boolean(report.link);
    const Icon = report.icon;
    const isFavorite = favorites.includes(report.id);

    return (
      <div
        role={isReady ? 'button' : undefined}
        tabIndex={isReady ? 0 : undefined}
        onClick={() => openReport(report)}
        onKeyDown={(e) => {
          if (isReady && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            openReport(report);
          }
        }}
        className={`group relative flex flex-col rounded-xl border p-4 transition-all ${
          isReady
            ? 'border-gray-200 bg-white hover:border-slate-300 hover:shadow-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-800 focus:ring-offset-2'
            : 'border-gray-100 bg-gray-50/80 cursor-default'
        }`}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div
            className={`p-2 rounded-lg ${
              isReady ? 'bg-slate-100 text-slate-700' : 'bg-gray-100 text-gray-400'
            }`}
          >
            <Icon className="h-5 w-5" />
          </div>
          <button
            type="button"
            onClick={(e) => toggleFavorite(report.id, e)}
            className={`p-1 rounded-md transition-colors ${
              isFavorite
                ? 'text-amber-500 hover:text-amber-600'
                : 'text-gray-300 hover:text-amber-400'
            }`}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Star className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
          </button>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3
              className={`text-sm font-semibold ${
                isReady ? 'text-slate-900 group-hover:text-slate-800' : 'text-gray-500'
              }`}
            >
              {report.title}
            </h3>
            {!isReady && (
              <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-gray-200 text-gray-600">
                Coming soon
              </span>
            )}
          </div>
          {showCategory && categoryTitle && (
            <p className="text-[11px] font-medium text-slate-400 mt-0.5">{categoryTitle}</p>
          )}
          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{report.description}</p>
        </div>

        {isReady && (
          <div className="mt-3 flex items-center text-xs font-medium text-slate-600 group-hover:text-slate-800">
            Open report
            <ChevronRight className="h-3.5 w-3.5 ml-0.5 transition-transform group-hover:translate-x-0.5" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-screen bg-gray-50 flex w-full overflow-hidden">
      <Sidebar />
      <main className="flex-1 p-6 md:p-8 overflow-y-auto min-w-0">
        <div className="w-full">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
            <p className="text-sm text-gray-500 mt-1">
              Financial statements and operational reports for your business.
            </p>
          </div>

          {/* Financial statements strip */}
          {!normalizedQuery && (
            <section className="mb-8">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                Financial statements
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {financialStatements.map((stmt) => {
                  const Icon = stmt.icon;
                  return (
                    <button
                      key={stmt.id}
                      type="button"
                      onClick={stmt.onClick}
                      className="group flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left hover:border-slate-300 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-slate-800 focus:ring-offset-2"
                    >
                      <div className="p-2 rounded-lg bg-slate-800 text-white shrink-0">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900">{stmt.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{stmt.description}</p>
                        <span className="inline-flex items-center mt-2 text-xs font-medium text-slate-600 group-hover:text-slate-800">
                          View in Accounts
                          <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Search */}
          <div className="relative mb-6 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              ref={searchRef}
              type="text"
              className="block w-full pl-10 pr-16 py-2.5 border border-gray-300 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-slate-800 text-sm"
              placeholder="Find report by name, description, or category"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <kbd className="hidden sm:inline-flex absolute inset-y-0 right-3 items-center px-1.5 text-[10px] font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded">
              /
            </kbd>
          </div>

          <div className="space-y-8 pb-12">
            {/* Favorites */}
            {!normalizedQuery && (
              <section>
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                  <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                  Favorites
                </h2>
                {favoriteReports.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {favoriteReports.map((report) => (
                      <ReportCard
                        key={`fav-${report.id}`}
                        report={report}
                        showCategory
                        categoryTitle={report.categoryTitle}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-6 text-center">
                    <Star className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Star reports you use often</p>
                    <p className="text-xs text-gray-400 mt-1">
                      They will appear here for quick access.
                    </p>
                  </div>
                )}
              </section>
            )}

            {/* Categories */}
            {categories.map((category) => {
              const filteredReports = category.reports.filter((r) =>
                matchesSearch(r, category.title)
              );
              if (filteredReports.length === 0) return null;

              const CategoryIcon = category.icon;

              return (
                <section key={category.id}>
                  <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                    <CategoryIcon className="h-4 w-4 text-gray-400" />
                    {category.title}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredReports.map((report) => (
                      <ReportCard key={report.id} report={{ ...report, categoryTitle: category.title, categoryId: category.id }} />
                    ))}
                  </div>
                </section>
              );
            })}

            {filteredCount === 0 && (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No reports found</h3>
                <p className="text-gray-500 mt-1 text-sm">
                  Nothing matches &ldquo;{searchQuery}&rdquo;. Try a different keyword.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Reports;
